import { useMemo } from "react";
import { Link } from "react-router-dom";
import {
  AlertOctagon,
  AlertTriangle,
  Calendar,
  Clock3,
  MessageSquareWarning,
  UserX,
} from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { useCanAccess, useTranslate } from "ra-core";

import { useDeskAttentionCounts } from "../contacts/useDeskAttention";
import { useRealEstateContext } from "../providers/RealEstateContext";

function contactsFilterHref(desk_queue: string) {
  return `/contacts?filter=${encodeURIComponent(JSON.stringify({ desk_queue }))}`;
}

function startOfWeekMonday(d: Date) {
  const date = new Date(d);
  const day = date.getDay();
  const diff = day === 0 ? -6 : 1 - day;
  date.setDate(date.getDate() + diff);
  date.setHours(0, 0, 0, 0);
  return date;
}

function isSameCalendarDay(a: Date, b: Date) {
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
}

export const NeedsAttentionCard = () => {
  const translate = useTranslate();
  const { canAccess: isManager } = useCanAccess({
    resource: "sales",
    action: "edit",
  });
  const { unassigned, slaOverdue, stale, needsWhatsAppReply, isPending } =
    useDeskAttentionCounts();
  const { siteVisits } = useRealEstateContext();

  const { todaysVisits, noShowsThisWeek } = useMemo(() => {
    const now = new Date();
    const weekStart = startOfWeekMonday(now);
    let today = 0;
    let noShow = 0;
    for (const visit of siteVisits) {
      const scheduled = new Date(visit.scheduled_at);
      if (visit.status === "Scheduled" && isSameCalendarDay(scheduled, now)) {
        today += 1;
      }
      if (visit.status === "No_Show" && scheduled >= weekStart) {
        noShow += 1;
      }
    }
    return { todaysVisits: today, noShowsThisWeek: noShow };
  }, [siteVisits]);

  const rows = [
    {
      key: "unassigned",
      label: translate("resources.contacts.filters.unassigned"),
      value: unassigned,
      icon: UserX,
      href: contactsFilterHref("unassigned"),
    },
    {
      key: "sla_overdue",
      label: translate("resources.contacts.filters.sla_overdue"),
      value: slaOverdue,
      icon: Clock3,
      href: contactsFilterHref("sla_overdue"),
    },
    {
      key: "needs_whatsapp_reply",
      label: translate("resources.contacts.filters.needs_whatsapp_reply"),
      value: needsWhatsAppReply,
      icon: MessageSquareWarning,
      href: "/inbox",
    },
    {
      key: "stale_48h",
      label: translate("resources.contacts.filters.stale_48h"),
      value: stale,
      icon: AlertTriangle,
      href: contactsFilterHref("stale_48h"),
    },
  ];

  const hasAnyAttention =
    rows.some((row) => row.value > 0) || noShowsThisWeek > 0;

  if (isPending) return null;

  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-base flex items-center gap-2">
          <AlertOctagon className="w-4 h-4 text-amber-600" />
          {translate("crm.dashboard.needs_attention.title", {
            _: "Needs attention",
          })}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-1">
        {rows.map((row) => (
          <Link
            key={row.key}
            to={row.href}
            className="flex items-center justify-between gap-2 rounded-md px-2 py-1.5 -mx-2 hover:bg-muted transition-colors"
          >
            <span className="flex items-center gap-2 text-sm text-foreground">
              <row.icon className="w-3.5 h-3.5 text-muted-foreground shrink-0" />
              {row.label}
            </span>
            <Badge
              variant={row.value > 0 ? "default" : "outline"}
              className="text-[11px] shrink-0"
            >
              {row.value}
            </Badge>
          </Link>
        ))}
        <Link
          to="/site_visits"
          className="flex items-center justify-between gap-2 rounded-md px-2 py-1.5 -mx-2 hover:bg-muted transition-colors"
        >
          <span className="flex items-center gap-2 text-sm text-foreground">
            <Calendar className="w-3.5 h-3.5 text-muted-foreground shrink-0" />
            {translate("crm.dashboard.needs_attention.todays_visits", {
              _: "Today's visits",
            })}
          </span>
          <Badge variant="outline" className="text-[11px] shrink-0">
            {todaysVisits}
          </Badge>
        </Link>
        <Link
          to="/site_visits"
          className="flex items-center justify-between gap-2 rounded-md px-2 py-1.5 -mx-2 hover:bg-muted transition-colors"
        >
          <span className="flex items-center gap-2 text-sm text-foreground">
            <AlertTriangle className="w-3.5 h-3.5 text-muted-foreground shrink-0" />
            {translate("crm.dashboard.needs_attention.no_shows_this_week", {
              _: "No-shows this week",
            })}
          </span>
          <Badge
            variant={noShowsThisWeek > 0 ? "destructive" : "outline"}
            className="text-[11px] shrink-0"
          >
            {noShowsThisWeek}
          </Badge>
        </Link>
        {!hasAnyAttention && (
          <p className="text-xs text-muted-foreground pt-1.5 px-2">
            {translate("crm.dashboard.needs_attention.all_clear", {
              _: "All caught up — nice work!",
            })}
          </p>
        )}
        {isManager && (unassigned > 0 || stale > 0) && (
          <p className="text-xs text-muted-foreground pt-1.5 px-2">
            {translate("crm.dashboard.needs_attention.assign_hint", {
              _: "Open a lead → Assign to agent (sets ownership so the agent can see it).",
            })}
          </p>
        )}
      </CardContent>
    </Card>
  );
};

export default NeedsAttentionCard;
